/**
 * Secure password generation utility.
 *
 * Uses a safe character set that excludes visually similar characters:
 * - Excluded: 0 (zero), O (capital O), o (lowercase o)
 * - Excluded: 1 (one), l (lowercase L), I (capital i), i (lowercase i)
 *
 * This prevents user confusion when reading/typing generated passwords.
 */

// Safe charset: 52 distinguishable alphanumeric characters
const SAFE_CHARSET =
  'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';

/**
 * Generate a cryptographically secure random password.
 *
 * @param length - Password length (default: 16)
 * @returns Generated password string
 */
export function generatePassword(length: number = 16): string {
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);

  let password = '';
  for (let i = 0; i < length; i++) {
    password += SAFE_CHARSET[array[i] % SAFE_CHARSET.length];
  }
  return password;
}

/**
 * Calculate password strength as a percentage (0-100).
 *
 * Factors considered:
 * - Length (up to 40 points)
 * - Lowercase letters (20 points)
 * - Uppercase letters (20 points)
 * - Numbers (20 points)
 *
 * @param password - Password to evaluate
 * @returns Strength score (0-100)
 */
export function calculatePasswordStrength(password: string): number {
  if (!password) return 0;

  let score = 0;

  // Length score (up to 40 points, ~2.5 per character, maxed at 16 chars)
  score += Math.min(password.length * 2.5, 40);

  // Character type bonuses
  if (/[a-z]/.test(password)) score += 20;
  if (/[A-Z]/.test(password)) score += 20;
  if (/[0-9]/.test(password)) score += 20;

  return Math.min(Math.round(score), 100);
}

/**
 * Get a human-readable strength label.
 *
 * @param strength - Strength score (0-100)
 * @returns Label: Weak, Fair, Good, Strong, or Excellent
 */
export function getStrengthLabel(strength: number): string {
  if (strength < 30) return 'Weak';
  if (strength < 50) return 'Fair';
  if (strength < 70) return 'Good';
  if (strength < 90) return 'Strong';
  return 'Excellent';
}

/**
 * Get the color class for a strength level.
 *
 * @param strength - Strength score (0-100)
 * @returns Tailwind color class
 */
export function getStrengthColor(strength: number): string {
  if (strength < 30) return 'bg-red-500';
  if (strength < 50) return 'bg-orange-500';
  if (strength < 70) return 'bg-yellow-500';
  if (strength < 90) return 'bg-green-500';
  return 'bg-emerald-500';
}
